import React, { useState, useRef } from 'react';

const SimplifiedSignatureTool = () => {
  const [activeTab, setActiveTab] = useState('approval'); // 'approval' or 'objection'
  const [objectionPdfFile, setObjectionPdfFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [signatureImage, setSignatureImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState({ message: '', type: '' });
  const [isDragOver, setIsDragOver] = useState(false);
  
  const objectionPdfInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  // Load PDF-lib dynamically
  const loadPDFLib = async () => {
    if (window.PDFLib) return window.PDFLib;
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js';
      script.onload = () => resolve(window.PDFLib);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const handleObjectionPdfUpload = (file) => {
    if (file && file.type === 'application/pdf') {
      setObjectionPdfFile(file);
      showStatus(`Objection Format PDF Loaded: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`, 'success');
    } else {
      showStatus('Please select a valid PDF file.', 'error');
    }
  };

  const handlePDFUpload = (file) => {
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      showStatus(`Approval Format PDF Loaded: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`, 'success');
    } else {
      showStatus('Please select a valid PDF file.', 'error');
    }
  };

  const handleSignatureUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSignatureImage({
          file: file,
          dataUrl: e.target.result
        });
        showStatus('Signature image uploaded successfully!', 'success');
      };
      reader.readAsDataURL(file);
    } else {
      setSignatureImage(null);
      showStatus('Please select a valid image file.', 'error');
    }
  };

  const fileToArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const processApprovalPDF = async () => {
    if (!pdfFile) {
      showStatus('Please upload an Approval Format PDF file.', 'error');
      return;
    }

    if (!signatureImage) {
      showStatus('Please upload a signature image.', 'error');
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);
      showStatus('Processing PDF...', 'info');

      // Load PDF-lib
      const PDFLib = await loadPDFLib();
      setProgress(10);

      // Load PDF
      const pdfArrayBuffer = await fileToArrayBuffer(pdfFile);
      const pdfDoc = await PDFLib.PDFDocument.load(pdfArrayBuffer);
      setProgress(25);

      // Get page dimensions (using first page as reference)
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { width, height } = firstPage.getSize();
      setProgress(40);

      // Convert signature image to PDF format
      const imageBytes = await fileToArrayBuffer(signatureImage.file);
      let image;

      if (signatureImage.file.type === 'image/png') {
        image = await pdfDoc.embedPng(imageBytes);
      } else {
        image = await pdfDoc.embedJpg(imageBytes);
      }

      setProgress(60);

      // Create a new page and add it as the last page
      const newPage = pdfDoc.addPage([width, height]);
      
      // Get image dimensions
      const imageDims = image.size();
      
      // Calculate scaling to fit the image on the page while maintaining aspect ratio
      const scaleX = width / imageDims.width;
      const scaleY = height / imageDims.height;
      const scale = Math.min(scaleX, scaleY) * 0.8; // 0.8 to add some margin
      
      const scaledWidth = imageDims.width * scale;
      const scaledHeight = imageDims.height * scale;
      
      // Center the image on the page (moved slightly up)
      const x = (width - scaledWidth) / 2;
      const y = (height - scaledHeight) / 2 + 50; // Added 50 to move image up

      // Add the signature image to the new page
      newPage.drawImage(image, {
        x: x,
        y: y,
        width: scaledWidth,
        height: scaledHeight,
      });

      setProgress(75);

      // Generate the modified PDF
      const pdfBytes = await pdfDoc.save();
      setProgress(90);

      // Create download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Trigger download
      const originalName = pdfFile.name.replace('.pdf', '');
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `${originalName}_signed.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Cleanup
      URL.revokeObjectURL(url);

      setProgress(100);
      showStatus('Signature page added successfully! Download started.', 'success');

      setTimeout(() => {
        setProgress(0);
      }, 2000);

    } catch (error) {
      console.error('Error processing PDF:', error);
      showStatus('Error processing PDF. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const processObjectionPDF = async () => {
    if (!objectionPdfFile) {
      showStatus('Please upload an Objection Format PDF file.', 'error');
      return;
    }

    if (!signatureImage) {
      showStatus('Please upload a signature image.', 'error');
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);
      showStatus('Processing PDF...', 'info');

      // Load PDF-lib
      const PDFLib = await loadPDFLib();
      setProgress(10);

      // Load the objection format PDF
      const pdfArrayBuffer = await fileToArrayBuffer(objectionPdfFile);
      const pdfDoc = await PDFLib.PDFDocument.load(pdfArrayBuffer);
      setProgress(25);

      // Get the last page (where signature should be placed)
      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];
      const { width, height } = lastPage.getSize();
      setProgress(40);

      // Convert signature image to PDF format
      const imageBytes = await fileToArrayBuffer(signatureImage.file);
      let image;

      if (signatureImage.file.type === 'image/png') {
        image = await pdfDoc.embedPng(imageBytes);
      } else {
        image = await pdfDoc.embedJpg(imageBytes);
      }

      setProgress(60);

      // Get image dimensions
      const imageDims = image.size();
      
      // Calculate scaling to fit the image on the page while maintaining aspect ratio
      const scaleX = width / imageDims.width;
      const scaleY = height / imageDims.height;
      const scale = Math.min(scaleX, scaleY) * 0.7; // Larger scale for signature
      
      const scaledWidth = imageDims.width * scale;
      const scaledHeight = imageDims.height * scale;
      
      // Center the signature on the last page
      const x = (width - scaledWidth) / 2;
      const y = (height - scaledHeight) / 2;

      // Add the signature image to the existing last page
      lastPage.drawImage(image, {
        x: x,
        y: y,
        width: scaledWidth,
        height: scaledHeight,
      });

      setProgress(75);

      // Generate the modified PDF
      const pdfBytes = await pdfDoc.save();
      setProgress(90);

      // Create download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Trigger download
      const originalName = objectionPdfFile.name.replace('.pdf', '');
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `${originalName}_signed.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Cleanup
      URL.revokeObjectURL(url);

      setProgress(100);
      showStatus('Signature added to objection format successfully! Download started.', 'success');

      setTimeout(() => {
        setProgress(0);
      }, 2000);

    } catch (error) {
      console.error('Error processing PDF:', error);
      showStatus('Error processing PDF. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const processPDF = () => {
    if (activeTab === 'approval') {
      processApprovalPDF();
    } else {
      processObjectionPDF();
    }
  };

  const showStatus = (message, type) => {
    setStatus({ message, type });
    if (type === 'success' || type === 'error') {
      setTimeout(() => {
        setStatus({ message: '', type: '' });
      }, 5000);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type === 'application/pdf') {
      handlePDFUpload(files[0]);
    }
  };

  const canProcess = (activeTab === 'approval' ? pdfFile : objectionPdfFile) && signatureImage && !isProcessing;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-center text-gray-800 mb-4">PDF Signature Tool</h1>
      <p className="text-center text-gray-600 mb-8">Add signatures to your PDF documents</p>
      
      {/* Tab Navigation */}
      <div className="flex justify-center mb-8">
        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => setActiveTab('approval')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'approval'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Approval Format
          </button>
          <button
            onClick={() => setActiveTab('objection')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'objection'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Objection Format
          </button>
        </div>
      </div>

      {/* Approval Format Content */}
      {activeTab === 'approval' && (
        <>
          {/* Approval PDF Upload Section */}
          <div 
            className={`mb-8 p-6 border-2 border-dashed rounded-lg text-center transition-colors ${
              isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <h3 className="text-xl font-semibold mb-4">Upload Approval Format PDF</h3>
            <p className="text-gray-600 mb-4">Drag and drop your PDF here or click to browse</p>
            <input
              ref={pdfInputRef}
              type="file"
              accept=".pdf"
              onChange={(e) => handlePDFUpload(e.target.files[0])}
              className="mb-4"
            />
            {pdfFile && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-green-800">
                  <strong>✓ Approval Format PDF Loaded:</strong> {pdfFile.name}<br />
                  <small>Size: {(pdfFile.size / 1024 / 1024).toFixed(2)} MB</small>
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Objection Format Content */}
      {activeTab === 'objection' && (
        <>
          {/* Objection PDF Upload Section */}
          <div className="mb-8 p-6 border border-gray-200 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Upload Objection Format PDF</h3>
            <p className="text-gray-600 mb-4">Upload the objection format PDF document</p>
            <input
              ref={objectionPdfInputRef}
              type="file"
              accept=".pdf"
              onChange={(e) => handleObjectionPdfUpload(e.target.files[0])}
              className="mb-4"
            />
            {objectionPdfFile && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-green-800">
                  <strong>✓ Objection Format PDF Loaded:</strong> {objectionPdfFile.name}<br />
                  <small>Size: {(objectionPdfFile.size / 1024 / 1024).toFixed(2)} MB</small>
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Hidden Old PDF Upload Section */}
      <div style={{ display: 'none' }}>
        {/* Keep for compatibility */}
      </div>

      {/* Signature Upload Section */}
      <div className="mb-8 p-6 border border-gray-200 rounded-lg">
        <h3 className="text-xl font-semibold mb-4">Upload Signature Image</h3>
        <p className="text-gray-600 mb-6">
          {activeTab === 'approval' 
            ? "Upload signature image that will be added as a new last page in your PDF (PNG, JPG, JPEG)"
            : "Upload signature image that will be added to the last page of the objection format PDF (PNG, JPG, JPEG)"
          }
        </p>
        
        <div className="border border-gray-200 p-4 rounded-lg bg-gray-50">
          <label className="block font-semibold text-gray-700 mb-2">
            Select Signature Image:
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleSignatureUpload(e.target.files[0])}
            className="w-full mb-3"
          />
          {signatureImage && (
            <div className="mt-3">
              <img
                src={signatureImage.dataUrl}
                alt="Signature preview"
                className="max-w-48 h-16 object-contain border border-gray-300 bg-white rounded p-2"
              />
              <p className="text-sm text-green-600 mt-1">
                ✓ Signature uploaded - will be {activeTab === 'approval' ? 'added as new last page' : 'added to existing last page'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Process Button */}
      <div className="text-center mb-6">
        <button
          onClick={processPDF}
          disabled={!canProcess}
          className={`px-8 py-3 rounded-lg font-semibold text-white transition-colors ${
            canProcess 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? 'Adding Signature...' : 'Add Signature & Download'}
        </button>
      </div>

      {/* Progress Bar */}
      {isProcessing && (
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-center text-sm text-gray-600 mt-2">{progress}% Complete</p>
        </div>
      )}

      {/* Status Message */}
      {status.message && (
        <div className={`p-4 rounded-lg text-center ${
          status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          status.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {status.message}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-800 mb-2">Instructions:</h4>
        <ol className="text-sm text-gray-600 space-y-1">
          {activeTab === 'approval' ? (
            <>
              <li>1. Upload your approval format PDF document</li>
              <li>2. Upload one signature image</li>
              <li>3. Click "Add Signature & Download" to get your PDF with signature page</li>
              <li>4. The signature image will be added as a completely new last page</li>
            </>
          ) : (
            <>
              <li>1. Upload your objection format PDF document</li>
              <li>2. Upload one signature image</li>
              <li>3. Click "Add Signature & Download" to get your signed PDF</li>
              <li>4. The signature will be centered on the existing last page with larger size</li>
            </>
          )}
        </ol>
        
        <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> {activeTab === 'approval' 
              ? 'The signature image will be automatically scaled and centered on a new page while maintaining its aspect ratio.'
              : 'The signature image will be centered and larger on the existing last page of your objection format PDF.'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default SimplifiedSignatureTool;